import type { DrawRules } from '../game/DrawRules.js';
import type { GameState } from '../game/GameState.js';
import type { Move, Piece, Square } from '../game/types.js';
import { BoardEvaluator } from './BoardEvaluator.js';
import { MoveEnumerator } from './MoveEnumerator.js';

const MATE_SCORE = 1_000_000;
const INF = MATE_SCORE * 10;

type TtFlag = 'exact' | 'lower' | 'upper';

interface TtEntry {
  readonly depth: number;
  readonly score: number;
  readonly flag: TtFlag;
  readonly bestMove: Move | null;
}

export interface SearchOptions {
  /** Max search depth. Iterative deepening fills up to this. */
  readonly maxDepth: number;
  /**
   * Soft time budget in ms. After a depth completes we stop if budget is
   * exhausted; we always finish whatever depth is in progress so the move
   * we return is the result of a fully-resolved iteration.
   */
  readonly timeBudgetMs?: number;
  /**
   * If true, iterative deepening is used up to `maxDepth`. If false, only
   * the exact depth is searched (no TT-priming benefit from shallower runs).
   */
  readonly iterativeDeepening?: boolean;
}

export interface SearchResult {
  readonly move: Move;
  readonly score: number;
  /** Deepest depth fully searched. */
  readonly depthReached: number;
  /** Nodes visited across all iterations (diagnostics). */
  readonly nodes: number;
}

/**
 * Negamax alpha-beta search with Zobrist hashing, a transposition table,
 * iterative deepening, and quiescence over capture chains.
 *
 * Russian Shashki specifics:
 *  - Captures are mandatory — there is no "stand pat" in quiescence.
 *  - Mid-jump promotion and flying kings are encoded inside `Move`, so the
 *    search just calls `state.applyMove(move)`; depth math is unaffected.
 *  - Mobility-zero on your turn is a loss (validator returns []); we score
 *    that as `-MATE + ply` so faster mates beat slower ones.
 */
export class MinimaxSearch {
  private readonly zobrist: Zobrist;
  private readonly table = new Map<string, TtEntry>();
  private nodes = 0;
  private deadline = Number.POSITIVE_INFINITY;
  private aborted = false;

  constructor(
    private readonly evaluator: BoardEvaluator,
    private readonly enumerator: MoveEnumerator,
    private readonly drawRules: DrawRules,
    boardSize: number,
  ) {
    this.zobrist = new Zobrist(boardSize);
  }

  /** Discard cached evaluations — call between games. */
  reset(): void {
    this.table.clear();
  }

  search(state: GameState, opts: SearchOptions): SearchResult | null {
    this.nodes = 0;
    this.aborted = false;
    this.deadline =
      opts.timeBudgetMs && opts.timeBudgetMs > 0
        ? performance.now() + opts.timeBudgetMs
        : Number.POSITIVE_INFINITY;

    const rootMoves = this.enumerator.enumerate(state);
    const firstMove = rootMoves[0];
    if (!firstMove) return null;

    const useId = opts.iterativeDeepening !== false;
    const startDepth = useId ? 1 : opts.maxDepth;
    let best: { move: Move; score: number; depthReached: number } = {
      move: firstMove,
      score: 0,
      depthReached: 0,
    };

    for (let depth = startDepth; depth <= opts.maxDepth; depth += 1) {
      const iteration = this.searchRoot(state, depth, firstMove, best.move);
      if (this.aborted) break;
      best = iteration;
      if (Math.abs(iteration.score) > MATE_SCORE - 1_000) break; // forced mate found
      if (performance.now() >= this.deadline) break;
    }
    return { ...best, nodes: this.nodes };
  }

  private searchRoot(
    state: GameState,
    depth: number,
    fallback: Move,
    pvHint: Move | null,
  ): { move: Move; score: number; depthReached: number } {
    const moves = this.enumerator.enumerateOrdered(state, pvHint);
    let alpha = -INF;
    const beta = INF;
    let bestMove = fallback;
    let bestScore = -INF;
    for (const m of moves) {
      const child = state.applyMove(m);
      const score = -this.negamax(child, depth - 1, -beta, -alpha, 1);
      if (this.aborted) return { move: bestMove, score: bestScore, depthReached: depth - 1 };
      if (score > bestScore) {
        bestScore = score;
        bestMove = m;
      }
      if (score > alpha) alpha = score;
    }
    return { move: bestMove, score: bestScore, depthReached: depth };
  }

  private negamax(
    state: GameState,
    depth: number,
    alphaIn: number,
    beta: number,
    ply: number,
  ): number {
    this.nodes += 1;
    if ((this.nodes & 1023) === 0 && performance.now() >= this.deadline) {
      this.aborted = true;
      return 0;
    }

    if (this.drawRules.detect(state)) return 0;

    const key = this.zobrist.hash(state);
    const tt = this.table.get(key);
    if (tt && tt.depth >= depth) {
      if (tt.flag === 'exact') return tt.score;
      if (tt.flag === 'lower' && tt.score >= beta) return tt.score;
      if (tt.flag === 'upper' && tt.score <= alphaIn) return tt.score;
    }

    if (depth <= 0) return this.quiescence(state, alphaIn, beta, ply);

    const moves = this.enumerator.enumerateOrdered(state, tt?.bestMove ?? null);
    if (moves.length === 0) return -MATE_SCORE + ply;

    let alpha = alphaIn;
    let best = -INF;
    let bestMove: Move | null = null;
    for (const m of moves) {
      const child = state.applyMove(m);
      const score = -this.negamax(child, depth - 1, -beta, -alpha, ply + 1);
      if (this.aborted) return 0;
      if (score > best) {
        best = score;
        bestMove = m;
      }
      if (score > alpha) alpha = score;
      if (alpha >= beta) break;
    }

    const flag: TtFlag =
      best <= alphaIn ? 'upper' : alpha >= beta ? 'lower' : 'exact';
    this.table.set(key, { depth, score: best, flag, bestMove });
    return best;
  }

  /**
   * Quiescence over capture chains. Russian rules make captures mandatory,
   * so there's no stand-pat — if any capture exists from this position, the
   * mover must take one, and we recurse until the position is quiet.
   */
  private quiescence(
    state: GameState,
    alphaIn: number,
    beta: number,
    ply: number,
  ): number {
    if (this.drawRules.detect(state)) return 0;
    const moves = this.enumerator.enumerate(state);
    if (moves.length === 0) return -MATE_SCORE + ply;
    const isCapture = (moves[0]?.captures.length ?? 0) > 0;
    if (!isCapture) {
      return this.evaluator.evaluate(state, state.currentPlayer);
    }
    let alpha = alphaIn;
    let best = -INF;
    for (const m of moves) {
      const child = state.applyMove(m);
      const score = -this.quiescence(child, -beta, -alpha, ply + 1);
      if (this.aborted) return 0;
      if (score > best) best = score;
      if (score > alpha) alpha = score;
      if (alpha >= beta) break;
    }
    return best;
  }
}

/**
 * Pre-tabulated 32-bit Zobrist keys, seeded by a Mulberry32 PRNG so the
 * keys are deterministic across runs (helpful for debugging TT behavior).
 * Two 32-bit halves are combined into a string key so collisions in JS's
 * 32-bit XOR don't bleed into the TT.
 */
class Zobrist {
  private readonly pieceKeysA: number[];
  private readonly pieceKeysB: number[];
  private readonly sideA: number;
  private readonly sideB: number;
  private readonly boardSize: number;

  constructor(boardSize: number) {
    this.boardSize = boardSize;
    const total = boardSize * boardSize * 4;
    const rng = mulberry32(0x9e3779b1);
    this.pieceKeysA = Array.from({ length: total }, () => rng() | 0);
    this.pieceKeysB = Array.from({ length: total }, () => rng() | 0);
    this.sideA = rng() | 0;
    this.sideB = rng() | 0;
  }

  hash(state: GameState): string {
    let a = 0;
    let b = 0;
    for (const { square, piece } of state.pieces()) {
      const idx = this.pieceIndex(square, piece);
      a ^= this.pieceKeysA[idx] ?? 0;
      b ^= this.pieceKeysB[idx] ?? 0;
    }
    if (state.currentPlayer === 'black') {
      a ^= this.sideA;
      b ^= this.sideB;
    }
    return `${a >>> 0}:${b >>> 0}`;
  }

  private pieceIndex(square: Square, piece: Piece): number {
    const kindOffset = piece.kind === 'king' ? 1 : 0;
    const colorOffset = piece.color === 'white' ? 0 : 2;
    return (square.row * this.boardSize + square.col) * 4 + colorOffset + kindOffset;
  }
}

function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) | 0;
    let x = t;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return (x ^ (x >>> 14)) >>> 0;
  };
}
